// What the threat-intelligence feed has been seeing lately, as a pie.
//
// It says nothing about THIS deployment - the title carries that, so the copy
// does not have to repeat it.
//
// Legend rows toggle. Striking one out removes its slice and the rest grow to
// fill the wheel, which is the point: the leaders are routinely two commodity
// botnets, and an analyst wants to see what the tail looks like without them.
// Percentages therefore re-base on what is still shown, because a slice that
// fills a third of the circle must not be labelled 12%.

import { useMemo, useState } from 'react';
import type { IntelActivityResponse, IntelTally } from '@/lib/api';
import { cn } from '@/lib/classnames';

/** The reasons the feed goes unasked, in the words a reader acts on. */
const SKIP_COPY: Record<string, string> = {
  not_configured:
    'This deployment holds no threat-intelligence credentials, so the feed was not asked. This is not a quiet week.',
  provider_unavailable:
    'The intelligence provider did not answer, so there is nothing to chart. This is a platform failure, not a finding.',
};

/** The slice colours, in the order they are handed out.
 *
 *  Token-backed so the wheel survives a theme flip. They are hues rather than
 *  severities on purpose: a family is an identity, and colouring the leader red
 *  would assert that the most COMMON family is the most dangerous one. */
const SLICE = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
  'var(--viz-6)',
  'var(--viz-7)',
  'var(--viz-8)',
];

const SIZE = 132;
const R = 60;
const CX = SIZE / 2;
const CY = SIZE / 2;

interface Slice extends IntelTally {
  color: string;
  path: string;
}

/** Turn counts into pie slices, largest first.
 *
 *  Exported for its own test: the arc maths is the part that silently produces
 *  a wrong picture rather than an error. */
export function sliceGeometry(rows: readonly IntelTally[]): Slice[] {
  const total = rows.reduce((n, r) => n + r.count, 0);
  if (total === 0) return [];

  let from = -Math.PI / 2; // 12 o'clock
  return rows.map((row, i) => {
    const sweep = (row.count / total) * Math.PI * 2;
    const to = from + sweep;
    const path = arc(from, to);
    from = to;
    return { ...row, color: SLICE[i % SLICE.length] ?? SLICE[0], path };
  });
}

/** One slice as a path. A slice at or past a half turn needs the large-arc
 *  flag, and getting that wrong renders the majority share as the minority. */
function arc(from: number, to: number): string {
  // A single slice covering the whole circle cannot be drawn as an arc - its
  // start and end points are identical, so the path collapses to nothing.
  if (to - from >= Math.PI * 2 - 1e-9) {
    return `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R} Z`;
  }
  const x1 = CX + R * Math.cos(from);
  const y1 = CY + R * Math.sin(from);
  const x2 = CX + R * Math.cos(to);
  const y2 = CY + R * Math.sin(to);
  const large = to - from > Math.PI ? 1 : 0;
  return `M ${CX} ${CY} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
}

export function TopFamilies({ activity, limit = 8 }: { activity: IntelActivityResponse; limit?: number }) {
  // Hidden by LABEL rather than by index: the list re-sorts on every refresh,
  // and an index would silently start striking out a different family.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const rows = useMemo(() => activity.families.slice(0, limit), [activity.families, limit]);
  const shown = useMemo(() => rows.filter((r) => !hidden.has(r.label)), [rows, hidden]);
  const slices = useMemo(() => sliceGeometry(shown), [shown]);
  const total = useMemo(() => shown.reduce((n, r) => n + r.count, 0), [shown]);

  // The colour follows the family, not its position in the visible list, so
  // striking one out does not recolour every row beneath it.
  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((r, i) => {
      map.set(r.label, SLICE[i % SLICE.length] ?? SLICE[0]);
    });
    return map;
  }, [rows]);

  function toggle(label: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      // Striking out everything would leave an empty wheel and no way back
      // except undoing each row, so the last visible family stays.
      return next.size === rows.length ? prev : next;
    });
  }

  const skip = SKIP_COPY[activity.skipped];
  if (skip) return <p className="text-muted text-sm">{skip}</p>;
  if (rows.length === 0) {
    return <p className="text-muted text-sm">The feed reported no attributed families in this window.</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg
        aria-label={`Top ${slices.length} malware families seen recently`}
        height={SIZE}
        role="img"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
      >
        <title>Top malware families in the wild</title>
        {slices.map((s) => (
          <path
            d={s.path}
            fill={colorOf.get(s.label) ?? s.color}
            key={s.label}
            style={{ transition: 'd 240ms ease, opacity 240ms ease' }}
          />
        ))}
      </svg>

      {/* The legend carries the names, beside the wheel rather than on it:
          family names are long and a label pinned to a thin slice overlaps its
          neighbour. Each row is a button because it does something. */}
      <ul className="flex-1 min-w-0 flex flex-col gap-0.5" style={{ minWidth: '11rem', listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((row) => {
          const off = hidden.has(row.label);
          const share = total > 0 && !off ? Math.round((row.count / total) * 100) : 0;
          return (
            <li key={row.label}>
              <button
                aria-pressed={!off}
                className="ring-focus w-full flex items-center gap-2 min-w-0 rounded px-1 py-1 text-left hover:bg-ink-900/60 transition-colors"
                onClick={() => toggle(row.label)}
                type="button"
              >
                <span
                  aria-hidden
                  className="shrink-0 rounded-sm transition-opacity"
                  style={{
                    background: colorOf.get(row.label),
                    width: '10px',
                    height: '10px',
                    opacity: off ? 0.25 : 1,
                  }}
                />
                <span
                  className={cn('text-sm truncate transition-colors', off && 'line-through text-ink-500')}
                  title={row.label}
                >
                  {row.label}
                </span>
                <span
                  className={cn(
                    'text-xs tabular-nums ml-auto shrink-0 transition-colors',
                    off ? 'text-ink-600' : 'text-muted',
                  )}
                >
                  {off ? '-' : `${share}%`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
